import { useEffect, useMemo, useRef, useState } from 'react'
import { AnimatePresence, motion } from 'framer-motion'
import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import { Button } from '../Common/Button'
import { Pressable } from '../Common/Pressable'

const MotionPressable = motion.create(Pressable)
import VolumeDownRoundedIcon from '@mui/icons-material/VolumeDownRounded'
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded'
import { FOCUS_GUARDIANS, type FocusParticleKind } from '../../utils/gamificationRegistry'
import { getAvatarById } from '../../utils/cosmeticCatalog'
import { emitTrackEvent, emitWorkAction } from '../../utils/gamificationEvents'
import { useGamification } from '../../context/useGamification'
import { useAuth } from '../../context/useAuth'
import { useWorkspace } from '../../context/useWorkspace'
import { switchedOffModulesFor } from '../../utils/workspaceModules'
import { fireAutomationEvent } from '../../services/automationBus'
import type { FocusHorizonTarget } from '../../context/focusHorizonContextCore'

type FocusHorizonOverlayProps = {
  target: FocusHorizonTarget | null
  onClose: () => void
}

type Phase = 'SETUP' | 'RUNNING' | 'DONE'

const DURATION_PRESETS = [5, 15, 25, 50]
const RING_RADIUS = 130
const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS

const formatClock = (totalSeconds: number) => {
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

const Particles = ({ kind, paused }: { kind: FocusParticleKind; paused?: boolean }) => {
  if (kind === 'NONE') return null
  const count = kind === 'CHARCOAL_SMOKE' ? 9 : 6
  return (
    <div
      className={`focusParticles focusParticles--${kind === 'CHARCOAL_SMOKE' ? 'smoke' : 'waves'}`}
      data-paused={paused ? 'true' : undefined}
      aria-hidden="true"
    >
      {Array.from({ length: count }, (_, index) => (
        <span key={index} style={{ '--i': index } as React.CSSProperties} />
      ))}
    </div>
  )
}

/**
 * Mounted fresh per target (keyed in the wrapper) so its state always starts
 * clean without a reset effect. Owns the countdown + audio + completion math.
 */
const FocusHorizonSession = ({ target, onClose }: { target: FocusHorizonTarget; onClose: () => void }) => {
  const { profile } = useGamification()
  const { currentUser } = useAuth()
  const { activeWorkspaceId, workspaces = [] } = useWorkspace()
  const activeWorkspace = workspaces.find((w) => w.id === activeWorkspaceId) ?? null
  const funPackOff = activeWorkspace ? switchedOffModulesFor(activeWorkspace).has('funPack') : false
  const [phase, setPhase] = useState<Phase>('SETUP')
  const [durationMin, setDurationMin] = useState(25)
  const [secondsLeft, setSecondsLeft] = useState(25 * 60)
  const [audioOn, setAudioOn] = useState(false)
  const [awardedMinutes, setAwardedMinutes] = useState(0)
  const audioRef = useRef<{ stop: () => void } | null>(null)
  const finishSprintRef = useRef<(elapsedSeconds: number) => void>(() => undefined)

  const guardian = useMemo(() => {
    if (funPackOff) return null
    const avatar = profile?.equippedAvatarId ? getAvatarById(profile.equippedAvatarId) : undefined
    return avatar?.guardianId ? FOCUS_GUARDIANS[avatar.guardianId] : null
  }, [funPackOff, profile?.equippedAvatarId])

  const particleKind: FocusParticleKind = guardian?.particle ?? 'NONE'
  const totalSeconds = durationMin * 60

  // Countdown tick — fires finishSprint inline at 0 instead of via a separate
  // effect, so we never set state synchronously in an effect body.
  useEffect(() => {
    if (phase !== 'RUNNING') return undefined
    const interval = window.setInterval(() => {
      setSecondsLeft((current) => {
        if (current <= 1) {
          window.clearInterval(interval)
          // Defer to a microtask so we're not setting state from inside the
          // same setSecondsLeft updater.
          window.setTimeout(() => finishSprintRef.current(totalSeconds), 0)
          return 0
        }
        return current - 1
      })
    }, 1000)
    return () => window.clearInterval(interval)
  }, [phase, totalSeconds])

  const stopAmbient = () => {
    audioRef.current?.stop()
    audioRef.current = null
  }

  // Tear down audio on unmount.
  useEffect(() => () => stopAmbient(), [])

  const startAmbient = () => {
    if (audioRef.current) return
    try {
      const AudioCtor = window.AudioContext
      if (!AudioCtor) return
      const ctx = new AudioCtor()
      const bufferSize = 2 * ctx.sampleRate
      const buffer = ctx.createBuffer(1, bufferSize, ctx.sampleRate)
      const channel = buffer.getChannelData(0)
      let last = 0
      for (let i = 0; i < bufferSize; i += 1) {
        const white = Math.random() * 2 - 1
        last = (last + 0.02 * white) / 1.02
        channel[i] = last * 3.2
      }
      const noise = ctx.createBufferSource()
      noise.buffer = buffer
      noise.loop = true
      const filter = ctx.createBiquadFilter()
      filter.type = 'lowpass'
      filter.frequency.value = guardian?.id === 'ZEN_MASTER' ? 520 : 1100
      const gain = ctx.createGain()
      gain.gain.value = 0
      noise.connect(filter)
      filter.connect(gain)
      gain.connect(ctx.destination)
      noise.start()
      gain.gain.linearRampToValueAtTime(0.07, ctx.currentTime + 1.2)
      audioRef.current = {
        stop: () => {
          try {
            gain.gain.linearRampToValueAtTime(0, ctx.currentTime + 0.3)
            noise.stop(ctx.currentTime + 0.4)
          } catch {
            /* already stopped */
          }
          window.setTimeout(() => void ctx.close().catch(() => undefined), 600)
        },
      }
    } catch (error) {
      console.warn('[FocusHorizon] ambient audio unavailable', error)
    }
  }

  const toggleAudio = () => {
    setAudioOn((current) => {
      const next = !current
      if (next) startAmbient()
      else stopAmbient()
      return next
    })
  }

  const finishSprint = (elapsedSeconds: number) => {
    const minutes = Math.floor(elapsedSeconds / 60)
    setAwardedMinutes(minutes)
    stopAmbient()
    setAudioOn(false)
    setPhase('DONE')
    if (minutes > 0) {
      emitTrackEvent('TRACK_FOCUS_TIME', minutes)
      if (target.workspaceId) {
        emitWorkAction({ kind: 'FOCUS_BLOCK', workspaceId: target.workspaceId, subNodeId: target.subNodeId })
        if (currentUser?.uid) {
          fireAutomationEvent(
            {
              type: 'FOCUS_BLOCK_COMPLETED',
              workspaceId: target.workspaceId,
              eventId: `focus-${target.subNodeId ?? 'block'}-${Date.now()}`,
              actorId: currentUser.uid,
              payload: { minutes, subNodeId: target.subNodeId },
            },
            { id: currentUser.uid },
          )
        }
      }
    }
  }
  // Keep the ref pointed at the latest closure so the countdown's microtask
  // dispatch sees the current `target` / state without re-arming the tick.
  useEffect(() => {
    finishSprintRef.current = finishSprint
  })

  const start = () => {
    setSecondsLeft(durationMin * 60)
    setPhase('RUNNING')
  }

  const endEarly = () => finishSprint(totalSeconds - secondsLeft)

  const close = () => {
    stopAmbient()
    onClose()
  }

  const progress = phase === 'SETUP' ? 1 : secondsLeft / totalSeconds
  const dashoffset = RING_CIRCUMFERENCE * (1 - progress)

  return (
    <motion.div
      className="focusHorizon"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
    >
          <Particles kind={particleKind} paused={phase === 'DONE'} />

          <MotionPressable
            className="focusExit"
            onClick={close}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
          >
            <CloseRoundedIcon fontSize="inherit" /> Exit Horizon
          </MotionPressable>

          <motion.div
            className="focusStage"
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.45, ease: 'easeOut' }}
          >
            <p className="focusEyebrow">Horizon Focus</p>
            <h1 className="focusTitle">{target.title}</h1>

            <div className="focusClockWrap">
              <svg className="focusRing" viewBox="0 0 300 300" width="300" height="300">
                <circle className="focusRingTrack" cx="150" cy="150" r={RING_RADIUS} />
                <circle
                  className="focusRingProgress"
                  cx="150"
                  cy="150"
                  r={RING_RADIUS}
                  strokeDasharray={RING_CIRCUMFERENCE}
                  strokeDashoffset={dashoffset}
                />
              </svg>
              <div className="focusClockFace">
                {phase === 'DONE' ? (
                  <>
                    <span className="focusDoneGlyph">✺</span>
                    <span className="focusClockSub">Sprint complete</span>
                  </>
                ) : (
                  <>
                    <span className="focusClockTime">{formatClock(phase === 'SETUP' ? totalSeconds : secondsLeft)}</span>
                    <span className="focusClockSub">{phase === 'RUNNING' ? 'Stay with it' : `${durationMin} min sprint`}</span>
                  </>
                )}
              </div>
            </div>

            {phase === 'SETUP' && (
              <>
                <div className="focusPresets">
                  {DURATION_PRESETS.map((preset) => (
                    <Pressable
                      key={preset}
                      aria-pressed={durationMin === preset}
                      className={`focusPreset ${durationMin === preset ? 'is-active' : ''}`}
                      onClick={() => {
                        setDurationMin(preset)
                        setSecondsLeft(preset * 60)
                      }}
                    >
                      {preset}m
                    </Pressable>
                  ))}
                </div>
                <Button variant="primary" size="lg" onClick={start}>
                  Begin Sprint
                </Button>
              </>
            )}

            {phase === 'RUNNING' && (
              <Button variant="tertiary" size="lg" onClick={endEarly}>
                End &amp; bank {Math.floor((totalSeconds - secondsLeft) / 60)} min
              </Button>
            )}

            {phase === 'DONE' && (
              <>
                <p className="focusReward">
                  {awardedMinutes > 0 ? `+${awardedMinutes} focus minutes logged.` : 'No full minute logged this time.'}
                </p>
                <Button variant="primary" size="lg" onClick={close}>
                  Return
                </Button>
              </>
            )}

            {guardian && (
              <Pressable aria-pressed={audioOn} className={`focusAudioToggle ${audioOn ? 'is-on' : ''}`} onClick={toggleAudio}>
                {audioOn ? <VolumeUpRoundedIcon fontSize="inherit" /> : <VolumeDownRoundedIcon fontSize="inherit" />} {guardian.audioLabel}
              </Pressable>
            )}
          </motion.div>
    </motion.div>
  )
}

export const FocusHorizonOverlay = ({ target, onClose }: FocusHorizonOverlayProps) => (
  <AnimatePresence>
    {target && <FocusHorizonSession key={target.subNodeId ?? target.title} target={target} onClose={onClose} />}
  </AnimatePresence>
)
